import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Clock, DollarSign, Phone, Users, Calendar, Send,
  TrendingUp, TrendingDown, ArrowRight, Settings2,
  MessageSquare, Sparkles, X, Check, Mail, Eye,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useCountUp(target, duration = 1200) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (target === 0 || target == null) { setVal(0); return; }
    let start = 0;
    const step = target / (duration / 16);
    const id = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(id); }
      else setVal(Math.round(start));
    }, 16);
    ref.current = id;
    return () => clearInterval(id);
  }, [target, duration]);
  return val;
}

function ChangeBadge({ value }) {
  if (value == null || value === 0) return null;
  const positive = value > 0;
  return (
    <Badge className={`text-[9px] gap-0.5 ${positive
      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
      : 'bg-red-500/10 text-red-400 border-red-500/20'
    }`} data-testid="change-badge">
      {positive ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {positive ? '+' : ''}{value}%
    </Badge>
  );
}

function MetricCard({ icon: Icon, iconColor, value, displayValue, label, prefix = '', suffix = '', change, large }) {
  const animated = useCountUp(value);
  const display = displayValue != null ? displayValue : `${prefix}${animated.toLocaleString()}${suffix}`;
  return (
    <div className={`flex flex-col ${large ? 'items-center text-center' : ''}`} data-testid={`roi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={large ? 16 : 13} className={iconColor} />
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className={`font-bold text-foreground ${large ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'}`}>
          {display}
        </span>
        <ChangeBadge value={change} />
      </div>
    </div>
  );
}

function SettingsPopover({ settings, onSave, onClose }) {
  const [price, setPrice] = useState(settings.avg_home_price || 450000);
  const [commission, setCommission] = useState((settings.commission_rate || 0.03) * 100);
  const [closeRate, setCloseRate] = useState((settings.close_rate || 0.04) * 100);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      avg_home_price: price,
      commission_rate: commission / 100,
      close_rate: closeRate / 100,
    });
    setSaving(false);
    onClose();
  };

  return (
    <div className="absolute right-0 top-full mt-1 z-50 w-64 rounded-lg border border-border bg-card shadow-xl p-3 space-y-3" data-testid="roi-settings-popover">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-foreground">ROI Settings</p>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={12} /></button>
      </div>
      <div className="space-y-2">
        <div>
          <label className="text-[10px] text-muted-foreground">Avg Home Price ($)</label>
          <input type="number" value={price} onChange={e => setPrice(+e.target.value)}
            className="w-full h-7 rounded bg-muted border border-border text-foreground text-xs px-2 mt-0.5 focus:outline-none focus:border-emerald-500/50"
            data-testid="settings-avg-price" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Commission Rate (%)</label>
          <input type="number" step="0.1" value={commission} onChange={e => setCommission(+e.target.value)}
            className="w-full h-7 rounded bg-muted border border-border text-foreground text-xs px-2 mt-0.5 focus:outline-none focus:border-emerald-500/50"
            data-testid="settings-commission" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Close Rate (%)</label>
          <input type="number" step="0.5" value={closeRate} onChange={e => setCloseRate(+e.target.value)}
            className="w-full h-7 rounded bg-muted border border-border text-foreground text-xs px-2 mt-0.5 focus:outline-none focus:border-emerald-500/50"
            data-testid="settings-close-rate" />
        </div>
      </div>
      <Button size="sm" onClick={handleSave} disabled={saving}
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-xs"
        data-testid="settings-save-btn">
        {saving ? 'Saving...' : <><Check size={11} className="mr-1" /> Save Settings</>}
      </Button>
    </div>
  );
}

export default function RoiImpactPanel() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [showSettings, setShowSettings] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [lastReportSent, setLastReportSent] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRoi = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/outcomes/roi?period=${p}`, { headers });
      setData(d);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchRoi(period); }, [period, fetchRoi]);

  useEffect(() => {
    axios.get(`${API}/weekly-email/log?limit=1`, { headers })
      .then(({ data: d }) => {
        if (d.logs?.length) setLastReportSent(d.logs[0].created_at);
      }).catch(() => {});
  }, [token]);

  const saveSettings = async (s) => {
    try {
      await axios.post(`${API}/outcomes/roi/settings`, s, { headers });
      toast.success('ROI settings saved');
      fetchRoi(period);
    } catch { toast.error('Failed to save settings'); }
  };

  const sendReport = async () => {
    setSendingReport(true);
    try {
      await axios.post(`${API}/weekly-email/trigger`, {}, { headers });
      toast.success('Your report is on the way!');
      setReportSent(true);
      setLastReportSent(new Date().toISOString());
      setTimeout(() => setReportSent(false), 15 * 60 * 1000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        toast.info(detail || 'Please wait before sending another report.');
      } else {
        toast.error(detail || 'Failed to send report.');
      }
    } finally {
      setSendingReport(false);
    }
  };

  if (loading && !data) {
    return (
      <Card className="border-border bg-card" data-testid="roi-impact-panel">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="h-6 w-6 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const m = data?.metrics || {};
  const c = data?.comparison || {};
  const hasData = data?.has_data;

  // Empty state
  if (!hasData) {
    return (
      <Card className="border-border bg-card" data-testid="roi-impact-panel">
        <CardContent className="p-8 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-2xl bg-emerald-500/8 border border-emerald-500/15 flex items-center justify-center mb-3">
            <Sparkles size={24} className="text-emerald-500/40" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1">Start using Agent9 to see your impact</h3>
          <p className="text-xs text-muted-foreground max-w-xs mb-4">
            Time saved, appointments booked, and estimated commission will appear here after your first calls.
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => navigate('/call-simulation')}
              className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/20 h-7 text-xs px-3"
              data-testid="roi-empty-sim-btn">
              <MessageSquare size={12} className="mr-1" /> Run Simulation
            </Button>
            <Button size="sm" variant="ghost" onClick={() => navigate('/calls')}
              className="text-muted-foreground hover:text-foreground h-7 text-xs px-3"
              data-testid="roi-empty-call-btn">
              <Phone size={12} className="mr-1" /> Make Test Call
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const timeSavedDisplay = m.time_saved_hours >= 1
    ? `${m.time_saved_hours}h`
    : `${Math.round(m.time_saved_minutes)}m`;

  return (
    <Card className="border-emerald-500/15 bg-gradient-to-br from-primary/5/20 to-card" data-testid="roi-impact-panel">
      <CardHeader className="pb-2 pt-3.5 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
            <DollarSign size={15} className="text-emerald-400" />
            Your AI Impact
          </CardTitle>
          <div className="flex items-center gap-1.5">
            {/* Period toggle */}
            <div className="flex items-center gap-0.5 bg-muted rounded-md p-0.5" data-testid="roi-period-toggle">
              {[{ k: '7d', l: '7D' }, { k: '30d', l: '30D' }, { k: '90d', l: '90D' }].map(p => (
                <button key={p.k} onClick={() => setPeriod(p.k)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                    period === p.k
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`roi-period-${p.k}`}>
                  {p.l}
                </button>
              ))}
            </div>
            {/* Settings */}
            <div className="relative">
              <button onClick={() => setShowSettings(!showSettings)}
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                data-testid="roi-settings-btn">
                <Settings2 size={13} />
              </button>
              {showSettings && (
                <SettingsPopover
                  settings={data?.settings || {}}
                  onSave={saveSettings}
                  onClose={() => setShowSettings(false)}
                />
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {/* Hero metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
          <MetricCard icon={Clock} iconColor="text-sky-400" value={m.time_saved_minutes}
            displayValue={timeSavedDisplay} label="Time Saved" change={c.time_saved_change} large />
          <MetricCard icon={DollarSign} iconColor="text-emerald-400" value={m.commission_influenced}
            prefix="$" label="Commission Influenced" change={c.commission_change} large />
          <div className="col-span-2 sm:col-span-1 flex items-center justify-center">
            <MetricCard icon={DollarSign} iconColor="text-violet-400" value={m.pipeline_value}
              prefix="$" label="Pipeline Value" large />
          </div>
        </div>

        {/* Secondary metrics row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-border">
          <MetricCard icon={Users} iconColor="text-sky-400" value={m.leads_handled}
            label="Leads" change={c.leads_change} />
          <MetricCard icon={Phone} iconColor="text-violet-400" value={m.calls_handled}
            label="Calls" change={c.calls_change} />
          <MetricCard icon={Calendar} iconColor="text-emerald-400" value={m.appointments_booked}
            label="Bookings" change={c.bookings_change} />
          <MetricCard icon={Send} iconColor="text-amber-400" value={m.followups_sent}
            label="Follow-Ups" />
        </div>

        {/* Trust messaging + CTA */}
        <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-border/30">
          <div className="flex items-center gap-1.5">
            <Button size="sm" onClick={sendReport} disabled={sendingReport || reportSent}
              className={`h-7 text-[10px] px-3 gap-1 ${reportSent
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 cursor-default'
                : 'bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/20'}`}
              data-testid="send-report-btn">
              {sendingReport ? (
                <><div className="h-2.5 w-2.5 rounded-full border border-sky-400 border-t-transparent animate-spin" /> Sending...</>
              ) : reportSent ? (
                <><Check size={10} /> Report Sent</>
              ) : (
                <><Mail size={10} /> Email Me This Week's Report</>
              )}
            </Button>
            {lastReportSent && (
              <span className="text-[9px] text-muted-foreground" data-testid="last-report-sent-badge">
                Last sent {new Date(lastReportSent).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at {new Date(lastReportSent).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={() => navigate('/report-settings')}
              className="text-muted-foreground hover:text-foreground h-7 text-[10px] px-2 gap-1"
              data-testid="preview-report-link">
              <Eye size={10} /> Preview
            </Button>
          </div>
          <Button size="sm" variant="ghost" onClick={() => navigate('/analytics')}
            className="text-emerald-400 hover:text-primary h-6 text-[10px] px-2 gap-1"
            data-testid="roi-view-details-btn">
            View Details <ArrowRight size={10} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
